<template>
  <CoreSwiper
    v-if="isMobile && (lpu.is_award_or_grand_prix_winner || lpu.lpu_loyalty_program || (!lpu.closed && lpu.license))"
    free-mode
    slides-per-view="auto"
    :has-pagination="false"
    :space-between="8"
    :placeholder-min-height="28"
    class="mx-n4 px-4 trust-badges"
  >
    <template #slides="{ slideAttrs }">
      <div
        v-for="item in badgeComponents"
        :key="item.name"
        v-bind="slideAttrs"
      >
        <component
          :is="item.component"
          v-bind="item.props"
          class="text-no-wrap"
        />
      </div>
    </template>
  </CoreSwiper>
  <div
    v-else-if="lpu.is_award_or_grand_prix_winner || lpu.lpu_loyalty_program || (!lpu.closed && lpu.license)"
    class="d-flex align-center gc-3"
  >
    <component
      :is="item.component"
      v-bind="item.props"
      v-for="(item) in badgeComponents"
      :key="item.name"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import AwardWinner from 'src/components/common/AwardWinner/AwardWinner.vue'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import useURLStore from 'src/store/useURLStore'
import useContextStore from 'src/store/useContextStore'
import type { CommonContextStore } from 'src/types/context'
import LpuLoyaltyProgram from 'src/components/common/LpuLoyaltyProgram/LpuLoyaltyProgram.vue'
import LicenseVerified from 'src/components/common/LicenseVerified/LicenseVerified.vue'
import CoreSwiper from 'src/core/CoreSwiper/CoreSwiper.vue'

const props = defineProps<{
  lpu: Lpu
}>()

const { context, urlLang }: CommonContextStore = useContextStore()
const { reverseFullL10N } = useURLStore()

const urlLpuPage = reverseFullL10N({
  name: 'lpu',
  args: {
    language: urlLang,
    town_translit: props.lpu.town.translit,
    lpu_translit: props.lpu.translit,
  },
})

const isMobile = useIsMobile()

const badgeComponents = computed(() => {
  const badges: Array<{
    component: any
    props: Record<string, unknown>
    name: string
  }> = []

  if (props.lpu.is_award_or_grand_prix_winner) {
    badges.push({
      component: AwardWinner,
      name: 'AwardWinner',
      props: {
        type: 'lpu',
        'redirect-url': `${urlLpuPage}#medals`,
        'current-award-year': context.site_settings.current_award_year,
        'is-award-or-grand-prix-winner': props.lpu.is_award_or_grand_prix_winner,
      },
    })
  }

  if (props.lpu.lpu_loyalty_program) {
    badges.push({
      component: LpuLoyaltyProgram,
      name: 'LpuLoyaltyProgram',
      props: {
        'bonus-levels': props.lpu.lpu_loyalty_program.levels,
        'max-payment-percent': props.lpu.lpu_loyalty_program.max_payment_percent,
        'welcome-bonus-amount': props.lpu.lpu_loyalty_program.welcome_bonus_amount,
        'use-badge': true,
      },
    })
  }

  if (!props.lpu.closed && props.lpu.license) {
    badges.push({
      component: LicenseVerified,
      name: 'LicenseVerified',
      props: {
        license: props.lpu.license,
      },
    })
  }

  return badges
})
</script>

<style lang="scss" scoped>
.trust-badges {
  width: auto;
}
</style>
